import _ from 'lodash';

import { getState } from './App';
import { userIsAdmin } from './Auth';
import { getIntl } from './IntlInstance';

export const getSettingsMap = () => {
  const intl = getIntl();

  const state = getState();
  if (!state) return [];
  const { profile } = state.Auth;

  return [
    {
      id: 'workspace',
      label: intl.formatMessage({ defaultMessage: 'Workspace' }),
      icon: 'fal fa-grid-2 fa-fw',
      items: [
        { id: 'branding', label: intl.formatMessage({ defaultMessage: 'Branding' }) },
        { id: 'watermark', label: intl.formatMessage({ defaultMessage: 'Watermark' }) },
        { id: 'date-and-time', label: intl.formatMessage({ defaultMessage: 'Date & time settings' }) },
        { id: 'filters', label: intl.formatMessage({ defaultMessage: 'Filters' }) },
        { id: 'dashboard', label: intl.formatMessage({ defaultMessage: 'Dashboard' }) },
        {
          id: 'sidebar',
          label: intl.formatMessage({ defaultMessage: 'Sidebar' }),
          title: intl.formatMessage({ defaultMessage: 'Customize workspace sidebar' }),
          description: intl.formatMessage({ defaultMessage: 'Hide navigation items for all workspace users.' }),
        },
      ],
    },
    {
      id: 'files-and-collections',
      label: intl.formatMessage({ defaultMessage: 'Files & Collections' }),
      icon: 'fal fa-folder-open fa-fw',
      items: [
        { id: 'ai-tagging', label: intl.formatMessage({ defaultMessage: 'AI Tagging' }) },
        { id: 'categories', label: intl.formatMessage({ defaultMessage: 'Tag Categories' }) },
        { id: 'custom-fields', label: intl.formatMessage({ defaultMessage: 'Custom Fields' }) },
        { id: 'visibility', label: intl.formatMessage({ defaultMessage: 'Visibility' }) },
        { id: 'file-dates-and-expiry', label: intl.formatMessage({ defaultMessage: 'File expirations' }) },
        { id: 'file-statuses', label: intl.formatMessage({ defaultMessage: 'File statuses' }) },
        { id: 'image-editor', label: intl.formatMessage({ defaultMessage: 'Image editor' }) },
        { id: 'downloading', label: intl.formatMessage({ defaultMessage: 'Downloading' }) },
        { id: 'file-commenting-and-favoriting', label: intl.formatMessage({ defaultMessage: 'File commenting & favoriting' }) },
        { id: 'full-screen-image-zoom', label: intl.formatMessage({ defaultMessage: 'Full screen image zoom' }) },
        { id: 'public-urls', label: intl.formatMessage({ defaultMessage: 'Public URLs' }) },
        { id: 'prefix-and-suffix', label: intl.formatMessage({ defaultMessage: 'File name prefix & suffix' }) },
        { id: 'search', label: intl.formatMessage({ defaultMessage: 'Search' }) },
      ],
    },
    {
      id: 'sharing',
      label: intl.formatMessage({ defaultMessage: 'Sharing' }),
      icon: 'fal fa-share-alt fa-fw',
      items: [
        { id: 'share-settings', label: intl.formatMessage({ defaultMessage: 'Share settings' }) },
      ],
    },
    {
      id: 'users-roles-teams',
      label: intl.formatMessage({ defaultMessage: 'Users, Roles & Teams' }),
      icon: 'fal fa-users fa-fw',
      items: [
        { id: 'users', label: intl.formatMessage({ defaultMessage: 'Users' }) },
        { id: 'roles', label: intl.formatMessage({ defaultMessage: 'Custom Roles' }) },
        { id: 'teams', label: intl.formatMessage({ defaultMessage: 'Teams' }) },
      ],
    },
    {
      id: 'security',
      label: intl.formatMessage({ defaultMessage: 'Security' }),
      icon: 'fal fa-lock fa-fw',
      items: [
        { id: '2fa', label: intl.formatMessage({ defaultMessage: 'Two-Factor Authentication' }) },
        { id: 'google-login', label: intl.formatMessage({ defaultMessage: 'Login with Google' }) },
      ],
    },
    {
      id: 'integrations',
      label: intl.formatMessage({ defaultMessage: 'Integrations' }),
      icon: 'fal fa-puzzle-piece fa-fw',
      items: [
        { id: 'integrations', label: intl.formatMessage({ defaultMessage: 'Connect tools' }) },
      ],
    },
    {
      id: 'advanced',
      label: intl.formatMessage({ defaultMessage: 'Advanced' }),
      icon: 'fal fa-wrench fa-fw',
      items: [
        { id: 'ftp', label: intl.formatMessage({ defaultMessage: 'FTP' }) },
        { id: 'iptc-and-exif-mapping', label: intl.formatMessage({ defaultMessage: 'IPTC & EXIF Mapping' }) },
        { id: 'sync-metadata', label: intl.formatMessage({ defaultMessage: 'Sync metadata' }) },
        { id: 'export', label: intl.formatMessage({ defaultMessage: 'Export' }) },
        { id: 'custom-pages', label: intl.formatMessage({ defaultMessage: 'Content Portals' }) },
      ],
    },
    {
      id: 'billing',
      label: intl.formatMessage({ defaultMessage: 'Billing & Subscription' }),
      icon: 'fal fa-credit-card fa-fw',
      items: [
        { id: 'owner-details', label: intl.formatMessage({ defaultMessage: 'Workspace owner' }) },
        {
          id: 'subscription',
          label: intl.formatMessage({
            defaultMessage: 'Subscription details',
          }),
          restrict: () => {
            if (profile.client.payment_disabled) return false;
            if (!userIsAdmin()) return false;
            return true;
          },
        },
      ],
    },
  ].map(section => ({
    ...section,
    items: section.items.filter(item => {
      if (item.restrict) return item.restrict();
      return true;
    }),
  }));
};

export const getSettingMapInfo = (id) => {
  const map = getSettingsMap();

  const flattened = _.flatMap(map, 'items');

  const setting = flattened.find(i => i.id === id);

  return setting ?? { id: null, label: '' };
};
